// services/badge-analytics.service.ts
// Comprehensive Badge Analytics for Business Users

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of, map } from 'rxjs';
import { environment } from 'src/environments/environment';

// ========================================
// INTERFACES
// ========================================

export interface BadgeOverview {
  totalBadges: number;
  totalUsers: number;
  totalAchieved: number;
  totalBonusPaid: number;
  achievementRate: number; // % of badges achieved
  period: string;
}

export interface BadgeConfig {
  id: number;
  businessHubId: number;
  enabled: boolean;
  requiredAmount: number;
  bonusCoins: number;
  badgeName: string;
  badgeDescription?: string;
  badgeIcon: string;
  badgeColor: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  stats?: {
    totalUsers: number;
    achievedCount: number;
    achievementRate: number;
  };
}

export interface BadgeConfigStats {
  totalUsers: number;
  achievedCount: number;
  achievementRate: number;
  topAchievers: BadgeTopAchiever[];
}

export interface BadgeTopAchiever {
  userId: string;
  userName: string;
  userAvatar?: string;
  achievedAt: string;
}

export interface UserBadge {
  userId: string;
  businessHubId: number;
  badgeConfigId: number;
  currentProgress: number;
  isAchieved: boolean;
  achievedAt?: string;
  bonusAwarded: boolean;
  config?: BadgeConfig;
  businessHub?: {
    id: number;
    name: string;
    avatar?: string;
    city?: string;
  };
  progressPercentage?: number;
}

export interface TopBadgeEarners {
  users: Array<{
    userId: string;
    userName: string;
    userAvatar?: string;
    totalBadges: number;
    achievedBadges: number;
    totalBonusEarned: number;
    achievementRate: number;
  }>;
}

// Cache Entry Interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  period?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BadgeAnalyticsService {
  private baseUrl = `${environment.apiUrl}/business/badges`;
  
  // Cache subjects for real-time updates
  private overviewCache$ = new BehaviorSubject<BadgeOverview | null>(null);
  private configsCache$ = new BehaviorSubject<BadgeConfig[] | null>(null);
  
  public overview$ = this.overviewCache$.asObservable();
  public configs$ = this.configsCache$.asObservable();

  // Cache storage with TTL
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(key: string, period?: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = (now - entry.timestamp) > this.CACHE_TTL;
    const periodMatches = !period || entry.period === period;

    return !isExpired && periodMatches;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, period?: string): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      period
    });
  }

  /**
   * Get cache entry
   */
  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? entry.data : null;
  }

  // ========================================
  // OVERVIEW & STATS
  // ========================================

  /**
   * Get overall badge analytics overview
   * @param forceRefresh Skip cache and force API call
   */
  getOverview(forceRefresh: boolean = false): Observable<BadgeOverview> {
    const cacheKey = 'badge_overview';
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<BadgeOverview>(cacheKey);
      if (cached) {
        this.overviewCache$.next(cached);
        return of(cached);
      }
    }

    return this.http.get<any>(`${this.baseUrl}/stats`).pipe(
      map(data => {
        // Transform to match BadgeOverview interface
        const overview: BadgeOverview = {
          totalBadges: data.totalBadges,
          totalUsers: data.totalUsers,
          totalAchieved: data.totalAchieved,
          totalBonusPaid: data.totalBonusPaid,
          achievementRate: data.totalBadges > 0 
            ? (data.totalAchieved / (data.totalBadges * Math.max(data.totalUsers, 1)) * 100) 
            : 0,
          period: 'all'
        };
        
        this.setCache(cacheKey, overview);
        this.overviewCache$.next(overview);
        return overview;
      })
    );
  }

  /**
   * Get all badge configurations with stats
   * @param forceRefresh Skip cache and force API call
   */
  getAllConfigs(forceRefresh: boolean = false): Observable<{ configs: BadgeConfig[]; total: number }> {
    const cacheKey = 'all_configs';
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<{ configs: BadgeConfig[]; total: number }>(cacheKey);
      if (cached) {
        this.configsCache$.next(cached.configs);
        return of(cached);
      }
    }

    return this.http.get<{ configs: BadgeConfig[]; total: number }>(`${this.baseUrl}/configs`).pipe(
      tap(data => {
        this.setCache(cacheKey, data);
        this.configsCache$.next(data.configs);
      })
    );
  }

  /**
   * Get hub-level badge statistics
   * @param forceRefresh Skip cache and force API call
   */
  getHubStats(forceRefresh: boolean = false): Observable<{
    totalBadges: number;
    totalUsers: number;
    totalAchieved: number;
    totalBonusPaid: number;
  }> {
    const cacheKey = 'hub_stats';
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<any>(cacheKey);
      if (cached) return of(cached);
    }

    return this.http.get<any>(`${this.baseUrl}/stats`).pipe(
      tap(data => this.setCache(cacheKey, data))
    );
  }

  // ========================================
  // CONFIG-SPECIFIC ANALYTICS
  // ========================================

  /**
   * Get statistics for a specific badge config
   * @param configId Badge config ID
   * @param forceRefresh Skip cache and force API call
   */
  getConfigStats(configId: number, forceRefresh: boolean = false): Observable<BadgeConfigStats> {
    const cacheKey = `config_stats_${configId}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<BadgeConfigStats>(cacheKey);
      if (cached) return of(cached);
    }

    return this.http.get<BadgeConfigStats>(`${this.baseUrl}/configs/${configId}/stats`).pipe(
      tap(data => this.setCache(cacheKey, data))
    );
  }

  // ========================================
  // TOP PERFORMERS
  // ========================================

  /**
   * Get top badge earners
   * @param limit Number of users to return
   * @param forceRefresh Skip cache and force API call
   */
  getTopEarners(limit: number = 10, forceRefresh: boolean = false): Observable<TopBadgeEarners> {
    const cacheKey = `top_earners_${limit}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<TopBadgeEarners>(cacheKey);
      if (cached) return of(cached);
    }

    // Since the backend doesn't have this endpoint yet, return empty data
    const topEarners: TopBadgeEarners = {
      users: []
    };
    this.setCache(cacheKey, topEarners);
    return of(topEarners);
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh all badge analytics data
   */
  refreshAllData(): void {
    this.getOverview(true).subscribe();
    this.getAllConfigs(true).subscribe();
    this.getHubStats(true).subscribe();
  }

  /**
   * Calculate badge health score (0-100)
   */
  calculateBadgeHealth(totalAchieved: number, totalBadges: number, totalUsers: number): number {
    if (totalBadges === 0 || totalUsers === 0) return 0;
    
    const maxPossible = totalBadges * totalUsers;
    const achievementScore = (totalAchieved / maxPossible) * 100;
    
    return Math.round(achievementScore);
  }

  /**
   * Get color for badge based on achievement rate
   */
  getBadgeColor(achievementRate: number): string {
    if (achievementRate >= 75) return '#10b981'; // green
    if (achievementRate >= 50) return '#3b82f6'; // blue
    if (achievementRate >= 25) return '#f59e0b'; // amber
    return '#64748b'; // slate
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.overviewCache$.next(null);
    this.configsCache$.next(null);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; entries: string[] } {
    const entries: string[] = [];
    this.cache.forEach((value, key) => {
      const age = Date.now() - value.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      entries.push(`${key} (${ageMinutes}m old)`);
    });

    return {
      size: this.cache.size,
      entries
    };
  }
}